import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import BaseModal from 'components/atoms/base-modal';
import DataflowIcon from 'components/atoms/icons/dataflow';
import classNames from 'classnames';
import BaseButton from 'components/atoms/baseButton';
import { stringToBoolean } from 'utils/helpers/string-to-boolean';
import BaseRadioButton from 'components/atoms/baseRadioButton';
import InfoIcon from 'components/atoms/icons/infoIcon';

import s from './styles.module.scss';
import InfoTooltip from './tooltip';

type BooleanString = 'true' | 'false';

interface IProps {
  opened: boolean;
  value: boolean;
  name: string;
  childCount?: number;
  onChange: (val: boolean) => void;
  onClose: () => void;
}

const ClinicReviewTimeModal: React.FC<IProps> = (props) => {
  const {
    opened,
    value: valueProps,
    name,
    childCount,
    onChange,
    onClose,
  } = props;

  const [value, setValue] = useState<BooleanString>('false');

  const noChild = !childCount;

  const { t } = useTranslation();

  const handleChangeValue = (val: string) => {
    setValue(val as BooleanString);
  };

  const handleClose = () => {
    onChange(valueProps);
    onClose();
  };

  const handleSave = () => {
    onChange(stringToBoolean(value) as boolean);
    onClose();
  };

  useEffect(() => {
    setValue(`${valueProps}`);
  }, [opened, valueProps]);

  return (
    <BaseModal
      title={t('labels.updateClinicReviewTime')}
      open={opened}
      onCancel={onClose}
      withFooter={[
        <BaseButton
          dataCy="cancel-button"
          key="cancel"
          type="secondary"
          label={t('controls.cancel')}
          onClick={handleClose}
        />,
        <BaseButton
          dataCy="add-time-button"
          key="add-time-button"
          type="primary"
          label={t('controls.apply')}
          onClick={handleSave}
        />,
      ]}
    >
      <div className={s.content}>
        <div className={s.block}>
          <div className={s.top} onClick={() => handleChangeValue('false')}>
            <BaseRadioButton
              id="applyToParentClinicOnly"
              name="applyToParentClinicOnly"
              value="false"
              onChange={handleChangeValue}
              checked={value === 'false'}
            />
            {t('labels.applyOnlyToCurrentClinic')}
            <InfoTooltip message={t('messages.currentOnlyUpdate')}>
              <InfoIcon />
            </InfoTooltip>
          </div>
          <div className={s.bottom}>{name}</div>
        </div>
        <div className={classNames(s.block, noChild && s.disabled)}>
          <div className={s.top} onClick={() => handleChangeValue('true')}>
            <BaseRadioButton
              id="applyToAllChildClinics"
              name="applyToAllChildClinics"
              value="true"
              onChange={handleChangeValue}
              checked={value === 'true'}
            />
            {noChild
              ? t('labels.noChildClinicsToUpdate')
              : t('labels.applyToCurrentAndChildClinics')}

            {!noChild && (
              <InfoTooltip message={t('messages.applyToAllUpdate')}>
                <InfoIcon />
              </InfoTooltip>
            )}
          </div>
          {!noChild && (
            <div className={classNames(s.bottom, s.child)}>
              {t('labels.willUpdate')}
              <span>
                <DataflowIcon />
                {childCount}
              </span>
              {t('labels.childClinics')}
            </div>
          )}
        </div>
        <div className={s['description-message']}>
          {t('messages.updateInBackgroundDescription')}
        </div>
      </div>
    </BaseModal>
  );
};

export default ClinicReviewTimeModal;
